import moment from 'moment';
import numeral from 'numeral';

import { CONDITION_OPTIONS } from '../constants';

let currencyRatio = 1;
let currencySymbol = '$';

export function setCurrencyInfo({ ratio = 1, symbol = '$' } = {}) {
  currencyRatio = ratio || 1;
  currencySymbol = symbol || '$';
}

function formatCurrencyAmount(numberValue, abbreviated) {
  const formatted = abbreviated
    ? numeral(numberValue).format('(0.00a)')
    : numeral(numberValue).format('0,0.00');

  if (formatted.startsWith('(') && formatted.endsWith(')')) {
    return `(${currencySymbol}${formatted.slice(1, -1)})`;
  }

  if (formatted.startsWith('-')) {
    return `-${currencySymbol}${formatted.slice(1)}`;
  }

  return `${currencySymbol}${formatted}`;
}

export function toCurrency(value, options = {}) {
  const config = {
    full: false,
    allowZero: true,
    ...options,
  };

  if ((!config.allowZero && value === 0)
    || value === undefined
    || value === null
    || value === Infinity
    || value === -Infinity) {
    return '';
  }

  let numberValue = value;

  if (typeof value !== 'number') {
    numberValue = parseFloat(value);
  }

  if (currencyRatio) {
    numberValue *= currencyRatio;
  }

  if (!config.full && numberValue >= 10000) {
    return formatCurrencyAmount(numberValue, true);
  }

  return formatCurrencyAmount(numberValue, false);
}

export function toPercentage(value, options = {}) {
  const config = {
    allowZero: true,
    ...options,
  };

  if ((!config.allowZero && value === 0)
    || value === undefined
    || value === null) {
    return '';
  }

  if (typeof value !== 'number') {
    return value;
  }

  return numeral(value).format('0,0.00%');
}

export function toNumber(value, options = {}) {
  const config = {
    allowZero: true,
    ...options,
  };

  if ((!config.allowZero && value === 0)
    || value === undefined
    || value === null) {
    return '';
  }

  return numeral(value).format('0,0');
}

export function toDecimal(value, options = {}) {
  const config = {
    allowZero: true,
    ...options,
  };

  if (config.allowZero && value === 0) {
    return '0.00';
  }

  if (!value) {
    return '';
  }

  if (typeof value !== 'number') {
    return value;
  }

  return value.toFixed(2);
}

export function toDate(value) {
  if (!value || (typeof value !== 'object' && typeof value !== 'string')) {
    return '';
  }

  return moment(value).format('ll');
}

export function toDateTime(value) {
  if (!value) {
    return '';
  }

  return moment(value).format('MM/DD/YYYY h:mm A');
}

export function toDuration(ms) {
  return numeral(ms / 1000).format('00:00:00');
}

export function toCardLabel(card) {
  const parts = [card.year, card.set, card.player];

  if (card.variation) {
    parts.push(card.variation);
  }

  if (card.number) {
    parts.push(`#${card.number}`);
  }

  if (card.grader && card.grade) {
    parts.push(getGradeDisplay(card.grader, card.grade));

    if (card.autographGrade) {
      parts.push(getAutographGradeDisplay(card.autographGrade));
    }

    if (card.qualifierType) {
      parts.push(card.qualifierType);
    }
  } else if (card.condition) {
    parts.push(card.condition);
  }

  return parts.join(' ');
}

export function toImageUrl(path) {
  return `https://firebasestorage.googleapis.com/v0/b/cardladder-71d53.appspot.com/o/${path.replace(/\//g, '%2F')}?alt=media`;
}

export function toEbayUrl(path, free = false) {
  let url = `https://ebay.com${path}${path.includes('?') ? '&' : '?'}mkcid=1&mkrid=711-53200-19255-0&siteid=0&customid=&toolid=10001&mkevt=1&campid=${free ? '5339032683' : '5339032677'}`;

  if (url.includes('/itm/')) {
    url += '&nordt=true&rt=nc&orig_cvip=true';
  }

  return url;
}

export function sortOtherGrades(otherGrades) {
  const results = [];

  CONDITION_OPTIONS.forEach(({ value }) => {
    let found = false;

    otherGrades = otherGrades.filter((item) => {
      if (!found && item.condition === value) {
        results.push(item);
        found = true;
        return false;
      }

      return true;
    });
  });

  return results;
}

export function formatData(value, format, options) {
  switch (format) {
    case 'currency':
      return toCurrency(value, options);
    case 'percentage':
      return toPercentage(value, options);
    case 'number':
      return toNumber(value, options);
    case 'decimal':
      return toDecimal(value, options);
    case 'date':
      return toDate(value);
    case 'datetime':
      return toDateTime(value);
    default:
      return value;
  }
}

export function dateToNum(d) {
  d = d.split('/');
  return Number(d[2] + d[0] + d[1]);
}

export function getDateFromString(dateString) {
  const [month, day, year] = dateString.split('/');

  return new Date(+year, +month - 1, +day);
}

export function getSortedDates(map, zoom) {
  let sortedDates = Object.keys(map);

  if (zoom) {
    const startDate = zoom.start ? moment(zoom.start) : moment().subtract(zoom, 'days');
    const endDate = zoom.end ? moment(zoom.end) : moment();

    sortedDates = sortedDates
      .filter((d) => {
        const date = moment(getDateFromString(d));

        return date > startDate && date < endDate;
      });
  }

  sortedDates.sort((a, b) => dateToNum(a) - dateToNum(b));

  return sortedDates;
}

export function getYearOptions(numYears = 75) {
  const yearOptions = [];
  let currentYear = new Date().getFullYear();

  for (let y = 0; y < numYears; y += 1) {
    yearOptions.push({ value: currentYear.toString(), label: currentYear.toString() });
    currentYear -= 1;
  }

  return yearOptions;
}

export function getTerapeakLink(title, international = false) {
  const cleanTitle = title
    .replace(/!/g, '')
    .replace(/\*/g, '')
    .replace(/([\uE000-\uF8FF]|\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDDFF])/g, '');

  const startDate = moment().subtract(1095, 'days').toDate().getTime();
  const endDate = moment().toDate().getTime();

  return `https://www.ebay.com/sh/research?dayRange=1095&keywords=${encodeURIComponent(cleanTitle)}&marketplace=${international ? 'ALL' : 'EBAY-US'}&tabName=SOLD&sorting=-datelastsold&startDate=${startDate}&endDate=${endDate}`;
}

export function getGraderFromCondition(condition) {
  if (!condition) {
    return null;
  }

  const lowerCaseCondition = condition.trim().toLowerCase();

  if (lowerCaseCondition.includes('psa')) {
    return 'psa';
  }

  if (lowerCaseCondition.includes('bgs') || lowerCaseCondition.includes('beckett') || lowerCaseCondition.includes('bvg')) {
    return 'beckett';
  }

  if (lowerCaseCondition.includes('sgc')) {
    return 'sgc';
  }

  if (lowerCaseCondition.includes('cgc') || lowerCaseCondition.includes('csg')) {
    return 'cgc';
  }

  return null;
}

export function getGradeDisplay(grader, grade) {
  const localGrader = grader.toUpperCase().replace('BECKETT', 'BGS');
  const gradeLabel = grade
    .replace('g', '')
    .replace('_', '.')
    .replace('auth', 'Auth')
    .replace('altered', 'Altered')
    .toUpperCase()
    .replace('10PRISTINE', '10 Pristine')
    .replace('10P', '10 Pristine')
    .replace('10B', '10 Black')
    .replace('10', '10 ')
    .trim();

  return `${localGrader} ${gradeLabel}`;
}

export function getAutographGradeDisplay(grade) {
  return 'Auto ' + grade
    .replace('g', '')
    .replace('_', '.')
    .replace('auth', 'Auth')
    .replace('altered', 'Altered')
    .toUpperCase()
    .replace('10', '10 ')
    .trim();
}

export function getConditionDisplay(grader, grade, autographGrade, qualifierType) {
  const parts = [];

  if (grader &&grade) {
    parts.push(getGradeDisplay(grader, grade));
  }

  if (autographGrade) {
    parts.push(getAutographGradeDisplay(autographGrade));
  }

  if (qualifierType) {
    parts.push(qualifierType);
  }

  return parts.join(' ');
}
